// Editor text printed as VECTOR OUTLINES (owner, 2026-10-17 — #1236). The text
// used to go to the PDF as a transparent picture (a PNG with a soft mask); the
// printer flattened the transparency and « THAT'S A WRAP » came back with a
// darker box on the sky. Here every letter is drawn as a filled vector shape,
// cut from the SAME font file the browser uses for the editor (vectorFaces.ts):
// no image, no mask, no opacity, no blend — nothing for a printer to flatten.
//
// Returns null when a letter has no linked face (a system font would draw it)
// — the caller then keeps the old raster for that element only.
import fontkit from '@pdf-lib/fontkit';
import { parseFontFaceCss, pickFace, type FontFaceRule } from './vectorFaces';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FkFont = any;

let facesPromise: Promise<FontFaceRule[]> | null = null;
/** Every @font-face of the Google Fonts stylesheets the app links (cached per page). */
export function linkedFaces(): Promise<FontFaceRule[]> {
  if (!facesPromise) {
    facesPromise = (async () => {
      if (typeof document === 'undefined') return [];
      const hrefs = [...document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href*="fonts.googleapis.com"]')].map((l) => l.href);
      const all: FontFaceRule[] = [];
      for (const h of hrefs) {
        try { const r = await fetch(h); if (r.ok) all.push(...parseFontFaceCss(await r.text())); } catch { /* that sheet's faces stay raster */ }
      }
      return all;
    })();
  }
  return facesPromise;
}

const fileCache = new Map<string, Promise<FkFont | null>>();
function fontFile(url: string): Promise<FkFont | null> {
  let p = fileCache.get(url);
  if (!p) {
    p = (async () => {
      try {
        const r = await fetch(url);
        if (!r.ok) return null;
        return fontkit.create(new Uint8Array(await r.arrayBuffer()));
      } catch { return null; }
    })();
    fileCache.set(url, p);
  }
  return p;
}

const instCache = new Map<string, FkFont>();
/**
 * Where the plain TTF of a family lives (the PDF's cover fonts, FONT_URLS).
 * ⚠️ A VARIABLE WOFF2 CANNOT BE INSTANCED by @pdf-lib/fontkit (getVariation
 * rebuilds it as a TTF over the WOFF2 stream: « reading 'tables' »), so a weight
 * other than the file's default is cut from the variable TTF instead.
 */
let ttfFor: (family: string, style: 'normal' | 'italic') => string | undefined = () => undefined;
export function setVectorTtfSource(fn: typeof ttfFor): void { ttfFor = fn; }

/** The file at the weight (and optical size) the browser draws it at. Null = cannot. */
async function instance(face: FontFaceRule, weight: number, opsz: number): Promise<FkFont | null> {
  const f = await fontFile(face.url);
  if (!f) return null;
  const axesOf = (font: FkFont) => (font.variationAxes ?? {}) as Record<string, { min: number; max: number; default: number }>;
  const wantFor = (axes: Record<string, { min: number; max: number; default: number }>) => {
    const w: Record<string, number> = {};
    if (axes.wght) w.wght = Math.min(axes.wght.max, Math.max(axes.wght.min, weight));
    if (axes.opsz) w.opsz = Math.min(axes.opsz.max, Math.max(axes.opsz.min, opsz));
    return w;
  };
  const axes = axesOf(f);
  const want = wantFor(axes);
  // Static file, or the variable file already at what is wanted: as it is.
  if (Object.entries(want).every(([k, v]) => Math.abs((axes[k]?.default ?? v) - v) < 0.5)) return f;
  const url = ttfFor(face.family, face.style);
  if (!url) return null;
  const key = `${url}|${weight}|${Math.round(opsz)}`;
  if (instCache.has(key)) return instCache.get(key)!;
  const ttf = await fontFile(url);
  if (!ttf) return null;
  let v: FkFont | null = null;
  try {
    const tw = wantFor(axesOf(ttf));
    v = Object.keys(tw).length ? ttf.getVariation(tw) : null;
  } catch { v = null; }
  instCache.set(key, v);
  return v;
}

/** Chrome's synthetic bold: a stroke of size × 1/24 (≤ 9 px) to 1/32 (≥ 36 px). */
export function fakeBoldRatio(px: number): number {
  if (px <= 9) return 1 / 24;
  if (px >= 36) return 1 / 32;
  return 1 / 24 + ((px - 9) / 27) * (1 / 32 - 1 / 24);
}
/** Chrome's synthetic oblique: x += y / 4. */
const SKEW = 0.25;

export interface VectorLine {
  /** Width of the line in points. */
  width: number;
  /** Path data per stroke need: `bold` parts get the synthetic-bold stroke. */
  build: (x0: number, baseline: number) => { plain: string; bold: string };
}

const n = (v: number) => (Math.round(v * 1000) / 1000).toString();

/**
 * One line of text as vector outlines. `sizePt`: the font size in points;
 * `px`: the same size in the canvas pixels the browser measured with (for the
 * synthetic-bold ratio and the optical size). Null = a letter has no face.
 */
export async function vectorLine(
  text: string, family: string, weight: number, style: 'normal' | 'italic', sizePt: number, px: number,
): Promise<VectorLine | null> {
  const faces = await linkedFaces();
  // Runs of letters drawn by the same face.
  const runs: Array<{ face: FontFaceRule; w: number; fakeBold: boolean; fakeItalic: boolean; text: string }> = [];
  for (const ch of text) {
    const cp = ch.codePointAt(0)!;
    const pick = /\s/.test(ch) && runs.length
      ? { face: runs[runs.length - 1].face, weight: runs[runs.length - 1].w, fakeBold: runs[runs.length - 1].fakeBold, fakeItalic: runs[runs.length - 1].fakeItalic }
      : pickFace(faces, family, weight, style, cp);
    if (!pick) return null;
    const last = runs[runs.length - 1];
    if (last && last.face === pick.face && last.w === pick.weight) last.text += ch;
    else runs.push({ face: pick.face, w: pick.weight, fakeBold: pick.fakeBold, fakeItalic: pick.fakeItalic, text: ch });
  }
  const laid: Array<{ font: FkFont; glyphs: FkFont[]; pos: FkFont[]; scale: number; fakeBold: boolean; fakeItalic: boolean; width: number }> = [];
  let width = 0;
  for (const r of runs) {
    const font = await instance(r.face, r.w, px);
    if (!font) return null;
    const run = font.layout(r.text);
    const scale = sizePt / font.unitsPerEm;
    let w = 0;
    for (const p of run.positions) w += p.xAdvance * scale;
    // A letter the file does not have (.notdef) would print a box: keep the raster.
    if (run.glyphs.some((g: FkFont, i: number) => g.id === 0 && !/\s/.test([...r.text][i] ?? ''))) return null;
    laid.push({ font, glyphs: run.glyphs, pos: run.positions, scale, fakeBold: r.fakeBold, fakeItalic: r.fakeItalic, width: w });
    width += w;
  }
  return {
    width,
    build: (x0: number, baseline: number) => {
      let plain = '', bold = '';
      let pen = x0;
      for (const r of laid) {
        r.glyphs.forEach((g: FkFont, i: number) => {
          const p = r.pos[i];
          const ox = pen + p.xOffset * r.scale, oy = p.yOffset * r.scale;
          let d = '';
          const pt = (x: number, y: number) => {
            const yy = y * r.scale + oy;
            const xx = x * r.scale + (r.fakeItalic ? yy * SKEW : 0);
            return `${n(ox + xx)} ${n(baseline - yy)}`;
          };
          for (const c of (g.path?.commands ?? []) as Array<{ command: string; args: number[] }>) {
            const a = c.args;
            if (c.command === 'moveTo') d += `M${pt(a[0], a[1])}`;
            else if (c.command === 'lineTo') d += `L${pt(a[0], a[1])}`;
            else if (c.command === 'quadraticCurveTo') d += `Q${pt(a[0], a[1])} ${pt(a[2], a[3])}`;
            else if (c.command === 'bezierCurveTo') d += `C${pt(a[0], a[1])} ${pt(a[2], a[3])} ${pt(a[4], a[5])}`;
            else if (c.command === 'closePath') d += 'Z';
          }
          if (r.fakeBold) bold += d; else plain += d;
          pen += p.xAdvance * r.scale;
        });
      }
      return { plain, bold };
    },
  };
}
